import { useEffect, useRef, useState } from "react";
import { Clock, ShieldCheck, XCircle, Upload, FileText, CheckCircle2, RefreshCw, Eye, Loader2, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { api, apiError } from "@/lib/api";
import { fdate } from "@/lib/format";
import { safeText } from "@/lib/safeText";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BANNER = {
  pendiente_aprobacion: {
    icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-800",
    titulo: "Tu cuenta está en verificación",
    texto: "En un plazo de 24 a 48 horas hábiles nos pondremos en contacto contigo para darte acceso a la plataforma.",
  },
  rechazada: {
    icon: XCircle, cls: "border-red-200 bg-red-50 text-red-800",
    titulo: "Tu solicitud requiere correcciones",
    texto: "Revisa el motivo, corrige o vuelve a cargar los documentos indicados y reenvía tu solicitud a revisión.",
  },
  rechazado_definitivo: {
    icon: Ban, cls: "border-slate-300 bg-slate-100 text-slate-700",
    titulo: "Solicitud cerrada",
    texto: "Tu solicitud de alta fue cerrada. Si consideras que es un error, contacta directamente a FELVORA.",
  },
};

export default function OnboardingPending() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});

  const load = () => api.get("/onboarding/mi-solicitud").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onPick = (tipo) => fileRefs.current[tipo]?.click();
  const onFile = async (tipo, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      await api.post("/onboarding/documentos", fd);
      toast.success("Documento cargado correctamente");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(""); }
  };

  const verDoc = async (docId) => {
    try {
      const { data: r } = await api.post(`/onboarding/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${r.url}`, "_blank", "noopener");
    } catch (err) { toast.error("No se pudo abrir el documento"); }
  };

  const reenviar = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/reenviar");
      toast.success("Solicitud reenviada a revisión");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!data)
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const estatus = data.estatus || "pendiente_aprobacion";
  const banner = BANNER[estatus] || BANNER.pendiente_aprobacion;
  const Icon = banner.icon;
  const esRechazada = estatus === "rechazada";
  const definitivo = estatus === "rechazado_definitivo";

  return (
    <div data-testid="onboarding-screen" className="mx-auto max-w-3xl px-4 py-8 md:py-12">
      <div className="mb-6 flex items-center justify-between">
        <VoraLogo variant="horizontal" tone="dark" />
        <Button data-testid="onboarding-refresh" variant="ghost" size="sm" onClick={load}>
          <RefreshCw className="mr-1.5 h-4 w-4" /> Actualizar estado
        </Button>
      </div>

      {/* Banner de estatus */}
      <div data-testid="onboarding-estatus" className={`flex items-start gap-3 rounded-lg border p-5 ${banner.cls}`}>
        <Icon className="mt-0.5 h-6 w-6 shrink-0" />
        <div>
          <h1 className="font-heading text-lg font-semibold">{banner.titulo}</h1>
          <p className="mt-1 text-sm opacity-90">{banner.texto}</p>
          <p className="mt-2 text-xs opacity-75">{safeText(data.nombre)} · {safeText(data.rfc)} · {safeText(data.regimen)}</p>
        </div>
      </div>

      {/* Motivo de rechazo */}
      {(esRechazada || definitivo) && data.motivo_rechazo && (
        <div data-testid="onboarding-motivo" className="mt-4 rounded-lg border border-red-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-500">Motivo</p>
          <p className="mt-1 text-sm text-slate-700">{data.motivo_rechazo}</p>
        </div>
      )}

      {/* Checklist de documentos */}
      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-sm font-semibold text-slate-800">Constancia de Situación Fiscal (SAT)</h2>
          <span className="text-xs text-slate-500">{data.completos}/{data.total} cargado</span>
        </div>
        <p className="mb-4 text-xs text-slate-400">
          Sube tu Constancia de Situación Fiscal vigente (PDF, JPG o PNG, máx. 15 MB). Es el único documento requerido para verificar tu cuenta.
        </p>
        <div className="space-y-3">
          {data.documentos.map((d) => (
            <div key={d.tipo} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-3">
              <div className="flex min-w-0 items-center gap-3">
                {d.subido ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /> : <FileText className="h-5 w-5 shrink-0 text-slate-400" />}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{d.label}</p>
                  {d.subido ? (
                    <p className="truncate text-xs text-slate-500">{d.nombre_archivo} · {fdate(d.fecha)}</p>
                  ) : (
                    <p className="text-xs text-amber-600">Pendiente de carga</p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {d.subido && (
                  <Button data-testid={`doc-view-${d.tipo}`} variant="ghost" size="sm" onClick={() => verDoc(d.doc_id)}>
                    <Eye className="mr-1 h-4 w-4" /> Ver
                  </Button>
                )}
                {!definitivo && (
                  <>
                    <input ref={(el) => (fileRefs.current[d.tipo] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden" onChange={(e) => onFile(d.tipo, e)} data-testid={`doc-input-${d.tipo}`} />
                    <Button data-testid={`doc-upload-${d.tipo}`} variant="outline" size="sm"
                      disabled={uploading === d.tipo} onClick={() => onPick(d.tipo)}>
                      {uploading === d.tipo ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                      {d.subido ? "Reemplazar" : "Subir"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {esRechazada && (
          <Button data-testid="onboarding-reenviar" onClick={reenviar} disabled={busy} className="mt-5 w-full bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Reenviar solicitud a revisión
          </Button>
        )}
      </div>

      <p className="mt-6 text-center text-xs text-slate-400">
        Hasta que tu alta sea aprobada, el acceso operativo (clientes, cartera, reportes) permanece bloqueado.
      </p>
    </div>
  );
}
